import clsx from "clsx";
import {
  FC,
  isValidElement,
  ReactElement,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import {
  CopyButton,
  isNewTabClick,
  useStickyScroll,
} from "@tsmono/react/components";
import { useProperty } from "@tsmono/react/hooks";
import { Icon, IconName } from "@tsmono/react/icons";

import { MessageLabel } from "../../chat/MessageLabel";
import {
  RowSelectCheckbox,
  useSelectableRow,
} from "../../selection/RowSelectCheckbox";
import { EventLabelContext } from "../EventLabelContext";
import { useStickyStuck } from "../hooks/useStickyStuck";
import type { EventPanelCallbacks } from "../types";

import { EventNavs } from "./EventNavs";
import styles from "./EventPanel.module.css";

interface EventPanelProps {
  eventNodeId: string;
  className?: string;
  title?: string;
  subTitle?: string;
  text?: string;
  icon?: IconName;
  children?: ReactNode | ReactNode[];
  childIds?: string[];
  collapsibleContent?: boolean;
  collapseControl?: "top" | "bottom";
  /** Use the muted background style (for span/step collapsible regions). */
  muted?: boolean;
  /** Depth of the event in the tree (0 = root). */
  depth?: number;
  /** Turn-navigation cluster for events with turn context (models AND their
   *  tools — tool headers are what is stuck for most scroll positions of
   *  tool-heavy transcripts, so navigation must stay reachable there):
   *  clickable turn label, prev/next chevrons, focus-view link. Actions come
   *  from `eventCallbacks` (onPrevTurn/onNextTurn/onTurnLabelClick/
   *  getEventFocusUrl); absent callbacks degrade to a passive label.
   *  `isAnchor` marks the turn's capstone (first flattened event): only its
   *  cluster is visible while unstuck; followers reveal theirs when pinned. */
  turnNav?: { turnNumber: number; totalTurns: number; isAnchor?: boolean };
  /** Inline content rendered between the title and the trailing nav/turn label (e.g. retry chip on retried model events). */
  headerExtra?: ReactNode;
  /** Collapse state and deep-link callbacks from the app store. */
  eventCallbacks?: EventPanelCallbacks;
}

interface ChildProps {
  "data-name"?: string;
}

/**
 * Renders a collapsible event panel with optional tabs, icons, and deep linking.
 */
export const EventPanel: FC<EventPanelProps> = ({
  eventNodeId,
  className,
  title,
  subTitle,
  text,
  icon,
  children,
  childIds,
  collapsibleContent,
  collapseControl = "top",
  muted,
  depth,
  turnNav,
  headerExtra,
  eventCallbacks,
}) => {
  const {
    onCollapse,
    getCollapsed,
    getEventUrl,
    linkingEnabled,
    getEventFocusUrl,
    onFocusTabChange,
    onPrevTurn,
    onNextTurn,
    onTurnLabelClick,
    onOpenEventFocus,
    getSelectedTab,
    onSelectTab,
    onTabSelected,
  } = eventCallbacks ?? {};
  const eventLabel = useContext(EventLabelContext);
  const rowSelection = useSelectableRow(eventNodeId);
  const selected = rowSelection?.selected === true;
  const externalCollapsed = getCollapsed?.(eventNodeId) ?? false;
  const collapsed = externalCollapsed;

  const setCollapsed = useCallback(
    (value: boolean) => {
      onCollapse?.(eventNodeId, value);
    },
    [onCollapse, eventNodeId]
  );

  const isCollapsible = (childIds || []).length > 0 || collapsibleContent;
  const useBottomDongle = isCollapsible && collapseControl === "bottom";

  const url =
    linkingEnabled && getEventUrl ? getEventUrl(eventNodeId) : undefined;

  const pillId = (index: number) => `${eventNodeId}-nav-pill-${index}`;

  const filteredArrChildren = (
    Array.isArray(children) ? children : [children]
  ).filter(Boolean);

  const defaultPill = filteredArrChildren.findIndex(
    (node) => hasDataDefault(node) && node.props["data-default"]
  );
  const defaultPillId = defaultPill !== -1 ? pillId(defaultPill) : pillId(0);

  // The pill nav and the collapsed-summary text share the same right-aligned
  // cell — they never appear together (summary text is only supplied for
  // single-child panels, which have no tabs).
  const showNavs =
    !(isCollapsible && collapsibleContent && collapsed) &&
    filteredArrChildren.length > 1;

  const [storedNav, setStoredNav] = useProperty(eventNodeId, "selectedNav", {
    defaultValue: defaultPillId,
  });

  // A caller-provided shared tab (by NAME) is remembered across events (e.g.
  // the single-event focus page, so navigating between turns keeps the same
  // tab open) instead of each event reverting to its own per-event selection.
  // Absent in the main transcript, where each panel keeps its own
  // `selectedNav`.
  const tabNameAt = (index: number): string | undefined => {
    const child = filteredArrChildren[index];
    return child && isValidElement<ChildProps>(child)
      ? child.props["data-name"]
      : undefined;
  };
  const tabNames = useMemo(
    () =>
      (Array.isArray(children) ? children : [children])
        .filter(Boolean)
        .map((child, index) =>
          isValidElement<ChildProps>(child)
            ? (child.props["data-name"] ?? `Tab ${index}`)
            : `Tab ${index}`
        ),
    [children]
  );
  const sharedTabActive = !!getSelectedTab && filteredArrChildren.length > 1;
  const sharedTab = sharedTabActive
    ? getSelectedTab(eventNodeId, tabNames)
    : undefined;
  // Pill for the shared tab name; falls back to the first pill when this event
  // doesn't have that tab (e.g. remembered "API" on a panel without an API tab).
  const sharedSelectedNav = pillId(
    Math.max(0, sharedTab === undefined ? -1 : tabNames.indexOf(sharedTab))
  );
  const selectedNav = sharedTabActive ? sharedSelectedNav : storedNav;
  const setSelectedNav = (target: string) => {
    if (sharedTabActive && onSelectTab) {
      const name = tabNameAt(Number(target.slice(target.lastIndexOf("-") + 1)));
      if (name) onSelectTab(eventNodeId, name);
    } else {
      setStoredNav(target);
    }
    // Only reachable from a user pill/picker click — align the panel so the
    // newly selected tab shows from its start (initial selection never calls this).
    onTabSelected?.(eventNodeId);
  };

  const stickyRef = useRef<HTMLDivElement>(null);
  const { stickyTop, scrollRef } = useStickyScroll();
  useStickyStuck(stickyRef, stickyTop, scrollRef);

  // The focus link carries this panel's selected tab so the focus page opens
  // on the same tab. The panel only hands over its own state (a tab NAME);
  // how that becomes a URL param is the host's contract, inside the builder.
  const selectedTabName = useMemo(
    () =>
      tabNames.length > 1
        ? tabNames[
            Number(selectedNav.slice(selectedNav.lastIndexOf("-") + 1)) || 0
          ]
        : undefined,
    [tabNames, selectedNav]
  );
  const focusUrl = turnNav
    ? getEventFocusUrl?.(eventNodeId, selectedTabName)
    : undefined;
  // Keep the host's tab registry current so the keyboard `f` entry builds the
  // same URL as the header link above. No unmount cleanup: the last-known tab
  // must survive virtualization unmounting the row (the panel's own selection
  // persists in the property bag the same way).
  const reportFocusTab = turnNav !== undefined;
  // eslint-disable-next-line tsmono/no-raw-use-effect -- baselined at rule introduction; migrate to a named hook or derived state
  useEffect(() => {
    if (reportFocusTab) onFocusTabChange?.(eventNodeId, selectedTabName);
  }, [onFocusTabChange, eventNodeId, selectedTabName, reportFocusTab]);

  const gridColumns: string[] = [];

  // evidence-selection checkbox
  if (rowSelection) {
    gridColumns.push("max-content");
  }

  // chevron
  if (isCollapsible && !useBottomDongle) {
    gridColumns.push("minmax(0, max-content)");
  }

  // icon
  if (icon) {
    gridColumns.push("max-content");
  }

  // title (carries copy-link + headerExtra so they wrap with the title)
  gridColumns.push("minmax(0, max-content)");
  // 1fr keeps this cell spanning free space (an `auto` track would collapse to the picker's width and never regrow); the 3rem floor stops it being squeezed to 0 by title/turnNav.
  gridColumns.push("minmax(3rem, 1fr)");
  // turn-navigation cluster
  if (turnNav) {
    gridColumns.push("max-content");
  }
  // search/reference label — far-right pill, like message numbering
  if (eventLabel) {
    gridColumns.push("max-content");
  }

  const toggleCollapse = useCallback(() => {
    setCollapsed(!collapsed);
  }, [setCollapsed, collapsed]);

  const [mouseOver, setMouseOver] = useState(false);

  const titleEl =
    eventLabel ||
    title ||
    icon ||
    rowSelection ||
    filteredArrChildren.length > 1 ? (
      <div
        title={subTitle}
        className={clsx(
          "text-size-small",
          mouseOver ? styles.hover : "",
          styles.stickyWrapper
        )}
        ref={stickyRef}
        style={{
          display: "grid",
          gridTemplateColumns: gridColumns.join(" "),
          columnGap: "0.3em",
          alignItems: "center",
          cursor: isCollapsible && !useBottomDongle ? "pointer" : undefined,
          // Sticky pin position (the `.stickyWrapper` class supplies
          // `position: sticky`); offset comes from the StickyScrollContext.
          top: stickyTop,
        }}
        onMouseEnter={() => setMouseOver(true)}
        onMouseLeave={() => setMouseOver(false)}
      >
        {rowSelection ? (
          <RowSelectCheckbox selection={rowSelection} item="event" />
        ) : null}
        {isCollapsible && !useBottomDongle ? (
          <button
            type="button"
            className={styles.collapseToggle}
            onClick={toggleCollapse}
            aria-expanded={!collapsed}
            aria-label={
              title
                ? `${collapsed ? "Expand" : "Collapse"} ${title}`
                : undefined
            }
          >
            <Icon name={collapsed ? "chevronRight" : "chevronDown"} />
          </button>
        ) : (
          ""
        )}
        {icon ? (
          <Icon
            name={icon}
            className="text-style-secondary"
            onClick={toggleCollapse}
          />
        ) : (
          ""
        )}
        {/* The header cells below widen the toggle's hit area for mouse users;
            the chevron button above is the control keyboard users operate. */}
        <div
          className={clsx(
            "text-style-secondary",
            "text-style-label",
            styles.title
          )}
          role="presentation"
          onClick={toggleCollapse}
        >
          <span>{title}</span>
          {headerExtra ? (
            <span
              className={styles.titleExtra}
              role="presentation"
              onClick={(e) => e.stopPropagation()}
            >
              {headerExtra}
            </span>
          ) : null}
          {url ? (
            <span role="presentation" onClick={(e) => e.stopPropagation()}>
              <CopyButton
                value={url}
                icon="link"
                className={clsx(styles.copyLink)}
              />
            </span>
          ) : null}
        </div>
        <div
          className={styles.navs}
          role="presentation"
          onClick={showNavs ? undefined : toggleCollapse}
        >
          {showNavs ? (
            <EventNavs
              navs={filteredArrChildren.map((child, index) => {
                const defaultTitle = `Tab ${index}`;
                const title =
                  child && isValidElement<ChildProps>(child)
                    ? child.props["data-name"] || defaultTitle
                    : defaultTitle;
                return {
                  id: `eventpanel-${eventNodeId}-${index}`,
                  title: title,
                  target: pillId(index),
                };
              })}
              selectedNav={selectedNav || ""}
              setSelectedNav={setSelectedNav}
            />
          ) : collapsed && text ? (
            <span className={clsx("text-style-secondary", styles.label)}>
              {text}
            </span>
          ) : null}
        </div>
        {turnNav ? (
          <span
            className={clsx(
              styles.turnNav,
              turnNav.isAnchor === false && styles.turnNavFollower
            )}
            role="presentation"
            onClick={(e) => e.stopPropagation()}
          >
            {onTurnLabelClick ? (
              <button
                type="button"
                className={clsx(styles.turnLabel, styles.turnLabelButton)}
                title="Go to turn (Ctrl+G)"
                aria-label={`Go to turn — currently turn ${turnNav.turnNumber} of ${turnNav.totalTurns}`}
                onClick={() => onTurnLabelClick(turnNav.turnNumber)}
              >
                {`turn ${turnNav.turnNumber}/${turnNav.totalTurns}`}
              </button>
            ) : (
              <span className={styles.turnLabel}>
                {`turn ${turnNav.turnNumber}/${turnNav.totalTurns}`}
              </span>
            )}
            {onNextTurn && (
              <button
                type="button"
                className={styles.turnButton}
                title="Next turn (j)"
                aria-label="Next turn"
                disabled={turnNav.turnNumber >= turnNav.totalTurns}
                onClick={() => onNextTurn(turnNav.turnNumber)}
              >
                <Icon name="chevronDown" />
              </button>
            )}
            {onPrevTurn && (
              <button
                type="button"
                className={styles.turnButton}
                title="Previous turn (k)"
                aria-label="Previous turn"
                disabled={turnNav.turnNumber <= 1}
                onClick={() => onPrevTurn(turnNav.turnNumber)}
              >
                <Icon name="chevronUp" />
              </button>
            )}
            {focusUrl && (
              <a
                className={styles.turnButton}
                href={focusUrl}
                // Without an in-window handler, fall back to a new tab; with one,
                // the href still serves ctrl/cmd/middle-click natively.
                target={onOpenEventFocus ? undefined : "_blank"}
                rel="noreferrer"
                title="Open focused turn view (f)"
                aria-label="Open focused turn view"
                onClick={(e) => {
                  if (onOpenEventFocus && !isNewTabClick(e)) {
                    e.preventDefault();
                    // The same href, so the tab param (and any future params)
                    // survive the in-window entry too.
                    onOpenEventFocus(focusUrl);
                  }
                }}
              >
                <Icon name="focusEnter" />
              </a>
            )}
          </span>
        ) : null}
        {eventLabel && (
          <MessageLabel label={eventLabel} className={styles.eventLabel} />
        )}
      </div>
    ) : (
      ""
    );

  // Determine root styling: depth === 0 or muted flag
  const isRoot = depth === 0 || muted;

  // A gutter (see `.expanded`) marks an event showing non-summary detail, so its
  // expanded extent stays visible while scrolling past it. For tabbed panels
  // that means a non-default (non-summary) tab is open; for single-content
  // panels it means the content is expanded. The summary/default view shows no
  // gutter.
  const hasTabs = filteredArrChildren.length > 1;
  const detailExpanded = hasTabs
    ? !collapsed && selectedNav !== defaultPillId
    : !!collapsibleContent && !collapsed;

  const card = (
    <div
      id={`event-panel-${eventNodeId}`}
      className={clsx(
        className,
        styles.card,
        isRoot ? styles.root : undefined,
        detailExpanded ? styles.expanded : undefined,
        selected ? styles.selected : undefined,
        eventCallbacks?.isJumpTarget?.(eventNodeId)
          ? styles.jumpTarget
          : undefined
      )}
    >
      {titleEl}
      <div
        className={clsx(
          "tab-content",
          styles.cardContent,
          isCollapsible && collapsed && collapsibleContent
            ? styles.hidden
            : undefined
        )}
      >
        {filteredArrChildren.map((child, index) => {
          const id = pillId(index);
          const isSelected = id === selectedNav;

          // Only render the selected tab (ported from inspect for better perf)
          if (!isSelected) {
            return null;
          }

          return (
            <div
              key={`children-${id}-${index}`}
              id={id}
              className={clsx("tab-pane", "show", "active")}
            >
              {child}
            </div>
          );
        })}
      </div>

      {isCollapsible && useBottomDongle ? (
        <button
          type="button"
          className={clsx(styles.bottomDongle, "text-size-smallest")}
          onClick={toggleCollapse}
          aria-expanded={!collapsed}
        >
          <Icon
            name={collapsed ? "chevronRight" : "chevronDown"}
            className={styles.dongleIcon}
          />
          transcript ({childIds?.length}{" "}
          {childIds?.length === 1 ? "event" : "events"})
        </button>
      ) : undefined}
    </div>
  );
  return card;
};

// Typeguard for reading default value from pills
interface DataDefaultProps {
  "data-default"?: boolean;
  [key: string]: unknown;
}

function hasDataDefault(
  node: ReactNode
): node is ReactElement<DataDefaultProps> {
  return (
    isValidElement(node) &&
    node.props !== null &&
    typeof node.props === "object" &&
    "data-default" in node.props
  );
}
